import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  makeStyles,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { green } from "@material-ui/core/colors";
import {
  FormatBold,
  FormatItalic,
  FormatStrikethrough,
  Code,
} from "@material-ui/icons";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    width: "100%",
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  textQuickAnswerContainer: {
    width: "100%",
  },
  formatToolbar: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    marginTop: 8,
  },
}));

const QuickAnswerSchema = Yup.object().shape({
  shortcut: Yup.string()
    .min(2, "Too Short!")
    .max(15, "Too Long!")
    .required("Required"),
  message: Yup.string()
    .min(8, "Too Short!")
    .max(30000, "Too Long!")
    .required("Required"),
});

const QuickAnswersModal = ({
  open,
  onClose,
  quickAnswerId,
  initialValues,
  onSave,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const messageInputRef = useRef(null);

  const wrapSelectionWithMarker = (setFieldValue, currentValue, marker) => {
    const value = currentValue || "";
    const input = messageInputRef.current;

    if (!input || input.selectionStart === undefined) {
      setFieldValue("message", `${value}${marker}texto${marker}`);
      return;
    }

    const { selectionStart, selectionEnd } = input;
    const selected = value.slice(selectionStart, selectionEnd) || "texto";
    const nextValue =
      value.slice(0, selectionStart) +
      marker +
      selected +
      marker +
      value.slice(selectionEnd);

    setFieldValue("message", nextValue);

    requestAnimationFrame(() => {
      input.focus();
      const cursorPos =
        selectionStart + marker.length + selected.length + marker.length;
      input.setSelectionRange(cursorPos, cursorPos);
    });
  };

  const initialState = {
    shortcut: "",
    message: "",
  };

  const [quickAnswer, setQuickAnswer] = useState(initialState);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchQuickAnswer = async () => {
      if (initialValues) {
        setQuickAnswer((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      if (!quickAnswerId) return;

      try {
        const { data } = await api.get(`/quickAnswers/${quickAnswerId}`);
        if (isMounted.current) {
          setQuickAnswer(data);
        }
      } catch (err) {
        toastError(err);
      }
    };

    void fetchQuickAnswer();
  }, [quickAnswerId, open, initialValues]);

  const handleClose = () => {
    onClose();
    setQuickAnswer(initialState);
  };

  const handleSaveQuickAnswer = async (values) => {
    try {
      if (quickAnswerId) {
        const { data } = await api.put(
          `/quickAnswers/${quickAnswerId}`,
          values
        );
        if (onSave) {
          onSave(data);
        }
        handleClose();
      } else {
        const { data } = await api.post("/quickAnswers", values);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("quickAnswersModal.success"));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle id="form-dialog-title">
          {quickAnswerId
            ? `${i18n.t("quickAnswersModal.title.edit")}`
            : `${i18n.t("quickAnswersModal.title.add")}`}
        </DialogTitle>
        <Formik
          initialValues={quickAnswer}
          enableReinitialize={true}
          validationSchema={QuickAnswerSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              void handleSaveQuickAnswer(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("quickAnswersModal.form.shortcut")}
                    name="shortcut"
                    autoFocus
                    error={touched.shortcut && Boolean(errors.shortcut)}
                    helperText={touched.shortcut && errors.shortcut}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    fullWidth
                  />
                </div>
                <div className={classes.formatToolbar}>
                  <Tooltip
                    title={i18n.t("quickAnswersModal.form.formatBold")}
                  >
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() =>
                        wrapSelectionWithMarker(
                          setFieldValue,
                          values.message,
                          "*"
                        )
                      }
                    >
                      <FormatBold fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={i18n.t("quickAnswersModal.form.formatItalic")}
                  >
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() =>
                        wrapSelectionWithMarker(
                          setFieldValue,
                          values.message,
                          "_"
                        )
                      }
                    >
                      <FormatItalic fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={i18n.t("quickAnswersModal.form.formatStrike")}
                  >
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() =>
                        wrapSelectionWithMarker(
                          setFieldValue,
                          values.message,
                          "~"
                        )
                      }
                    >
                      <FormatStrikethrough fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={i18n.t("quickAnswersModal.form.formatCode")}
                  >
                    <IconButton
                      type="button"
                      size="small"
                      onClick={() =>
                        wrapSelectionWithMarker(
                          setFieldValue,
                          values.message,
                          "```"
                        )
                      }
                    >
                      <Code fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <div className={classes.textQuickAnswerContainer}>
                  <Field
                    as={TextField}
                    label={i18n.t("quickAnswersModal.form.message")}
                    name="message"
                    error={touched.message && Boolean(errors.message)}
                    helperText={touched.message && errors.message}
                    variant="outlined"
                    margin="dense"
                    className={classes.textField}
                    multiline
                    rows={5}
                    fullWidth
                    inputRef={messageInputRef}
                  />
                </div>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("quickAnswersModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {quickAnswerId
                    ? `${i18n.t("quickAnswersModal.buttons.okEdit")}`
                    : `${i18n.t("quickAnswersModal.buttons.okAdd")}`}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default QuickAnswersModal;
